import { useState, useCallback, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import useWebSocket from '../hooks/useWebSocket';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import MapComponent from '../components/MapComponent';
import { getApiUrl } from '../utils/api';
import { 
  Navigation, Clock, CheckSquare, ArrowRight, 
  Map, LogOut, Check, MapPin, Edit2, Truck, CheckCircle, Play,
  Calendar, ChevronLeft, ChevronRight, MessageCircle
} from 'lucide-react';

// Helper to get API URL dynamically at call time (supports custom domains)
const getAPI = () => getApiUrl();

export default function DriverView() {
  const { user, token, logout } = useAuth();
  const [destinations, setDestinations] = useState([]);
  const [route, setRoute] = useState(null);
  const [startLocation, setStartLocation] = useState(null);
  const [activeStop, setActiveStop] = useState(0);
  const [mapCollapsed, setMapCollapsed] = useState(false);
  const [myLocation, setMyLocation] = useState(null);
  const [activeTab, setActiveTab] = useState('route'); // 'route' or 'calendar'
  
  // Calendar
  const [highlightedDates, setHighlightedDates] = useState([]);
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  
  // Note modal
  const [noteModalOpen, setNoteModalOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [editingNote, setEditingNote] = useState('');
  
  // Route state
  const [routeStarted, setRouteStarted] = useState(false);
  const [completeConfirmOpen, setCompleteConfirmOpen] = useState(false);
  
  // Location tracking
  const watchIdRef = useRef(null);
  const locationIntervalRef = useRef(null);

  // Handle WebSocket messages from admin
  const handleWsMessage = useCallback((data) => {
    if (data.type === 'route_update') {
      setDestinations(data.data?.destinations || []);
      setStartLocation(data.data?.start_location);
      setRoute(data.data?.route_data);
      toast.info('Route updated by admin');
    } else if (data.type === 'stop_update') {
      // This could be from admin editing notes
      setDestinations(prev => prev.map(d => 
        d.id === data.stop_id ? { ...d, completed: data.completed, note: data.note ?? d.note } : d
      ));
    } else if (data.type === 'route_cleared') {
      setDestinations([]);
      setRoute(null);
      setStartLocation(null);
      toast.info('Route cleared by admin');
    }
  }, []);

  const { isConnected, send } = useWebSocket(token, handleWsMessage);

  // Load active route on mount
  useEffect(() => {
    const loadRoute = async () => {
      try {
        const response = await axios.get(`${getAPI()}/active-route`);
        setDestinations(response.data.destinations || []);
        setStartLocation(response.data.start_location);
        setRoute(response.data.route_data);
        
        // Check if route was already started
        if (response.data.started_at) {
          setRouteStarted(true);
        }
        
        // Find first uncompleted stop
        const firstUncompleted = (response.data.destinations || []).findIndex(d => !d.completed);
        setActiveStop(firstUncompleted >= 0 ? firstUncompleted : 0);
      } catch (error) {
        console.error('Failed to load route:', error);
      }
    };
    
    // Load immediately on mount
    loadRoute();
  }, []);

  // Start location tracking
  useEffect(() => {
    if (!navigator.geolocation) {
      console.warn('Geolocation not supported');
      return;
    }

    // Get initial position
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setMyLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude });
      },
      (err) => console.warn('Geolocation error:', err),
      { enableHighAccuracy: true }
    );

    // Watch position continuously
    watchIdRef.current = navigator.geolocation.watchPosition(
      (pos) => {
        const newLoc = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        setMyLocation(newLoc);
      },
      (err) => console.warn('Watch position error:', err),
      { enableHighAccuracy: true, maximumAge: 10000, timeout: 10000 }
    );

    // Send location to admin periodically (every 10 seconds)
    locationIntervalRef.current = setInterval(async () => {
      if (myLocation) {
        try {
          await axios.post(`${getAPI()}/active-route/location`, myLocation);
        } catch (error) {
          console.error('Failed to send location:', error);
        }
      }
    }, 10000);

    return () => {
      if (watchIdRef.current) {
        navigator.geolocation.clearWatch(watchIdRef.current);
      }
      if (locationIntervalRef.current) {
        clearInterval(locationIntervalRef.current);
      }
    };
  }, []);

  // Load and poll calendar
  useEffect(() => {
    const loadCalendar = async () => {
      try {
        const response = await axios.get(`${getAPI()}/calendar`);
        setHighlightedDates(response.data.highlighted_dates || []);
      } catch (error) {
        console.error('Failed to load calendar:', error);
      }
    };
    loadCalendar();
    
    // Poll calendar every 10 seconds for sync
    const calendarPoll = setInterval(loadCalendar, 10000);
    return () => clearInterval(calendarPoll);
  }, []);

  // Toggle calendar date
  const toggleCalendarDate = useCallback(async (dateStr) => {
    setHighlightedDates(prev => 
      prev.includes(dateStr) 
        ? prev.filter(d => d !== dateStr)
        : [...prev, dateStr]
    );
    
    try {
      const response = await axios.post(`${getAPI()}/calendar/toggle`, { date: dateStr });
      setHighlightedDates(response.data.highlighted_dates || []);
    } catch (error) {
      console.error('Failed to toggle date:', error);
      try {
        const response = await axios.get(`${getAPI()}/calendar`);
        setHighlightedDates(response.data.highlighted_dates || []);
      } catch (e) {}
    }
  }, []);

  // Send location when it changes significantly
  useEffect(() => {
    if (myLocation && isConnected) {
      send({ type: 'driver_location', lat: myLocation.lat, lng: myLocation.lng });
    }
  }, [myLocation, isConnected, send]);

  // Toggle stop complete
  const toggleComplete = useCallback(async (index) => {
    // Read latest destinations via ref-style functional update
    let dest = null;
    let currentDestinations = [];
    setDestinations(prev => {
      currentDestinations = prev;
      dest = prev[index];
      return prev; // no change yet
    });

    // Allow state to flush
    await new Promise(r => setTimeout(r, 0));

    if (!dest || !dest.id) {
      toast.error('Stop not found. Please refresh.');
      return;
    }

    const newCompleted = !dest.completed;

    // Optimistic update
    setDestinations(prev => prev.map((d, i) =>
      i === index ? { ...d, completed: newCompleted, completed_at: newCompleted ? new Date().toISOString() : null } : d
    ));

    try {
      await axios.put(`${getAPI()}/active-route/stop/${dest.id}`, { completed: newCompleted, note: dest.note || null });

      if (newCompleted) {
        setDestinations(prev => {
          const nextUncompleted = prev.findIndex((d, i) => i > index && !d.completed);
          if (nextUncompleted !== -1) setActiveStop(nextUncompleted);
          return prev;
        });
        toast.success('Stop completed!');
      }
    } catch (error) {
      console.error('Toggle complete error:', error.response?.data || error);
      // Revert optimistic update
      setDestinations(prev => prev.map((d, i) =>
        i === index ? { ...d, completed: !newCompleted } : d
      ));
      // Reload fresh data from server
      try {
        const response = await axios.get(`${getAPI()}/active-route`);
        setDestinations(response.data.destinations || []);
      } catch (e) {}
      toast.error('Failed to update stop. Try again.');
    }
  }, []);

  // Open note editor
  const openNoteModal = useCallback((index) => {
    setEditingIndex(index);
    setEditingNote(destinations[index]?.note || '');
    setNoteModalOpen(true);
  }, [destinations]);

  // Save note
  const saveNote = useCallback(async () => {
    if (editingIndex === null) return;
    const dest = destinations[editingIndex];
    if (!dest || !dest.id) {
      toast.error('Stop not found. Please refresh.');
      setNoteModalOpen(false);
      return;
    }
    
    const note = editingNote.trim() || null;
    
    // Optimistic update
    setDestinations(prev => prev.map((d, i) => 
      i === editingIndex ? { ...d, note } : d
    ));
    setNoteModalOpen(false);
    
    try {
      await axios.put(`${getAPI()}/active-route/stop/${dest.id}`, { completed: dest.completed, note });
      toast.success('Note saved');
    } catch (error) {
      console.error('Save note error:', error.response?.data || error);
      // Reload route to get fresh data
      try {
        const response = await axios.get(`${getAPI()}/active-route`);
        setDestinations(response.data.destinations || []);
      } catch (e) {}
      toast.error('Failed to save note. Try again.');
    }
  }, [editingIndex, editingNote, destinations]);

  // Navigate to stop using Waze
  const navigateToStop = useCallback((dest) => {
    const wazeUrl = `https://waze.com/ul?ll=${dest.lat},${dest.lng}&navigate=yes`;
    window.open(wazeUrl, '_blank');
  }, []);

  // Start route - updates the start time
  const startRoute = useCallback(async () => {
    try {
      await axios.post(`${getAPI()}/active-route/start`);
      setRouteStarted(true);
      toast.success('Route started!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start route');
    }
  }, []);

  // Complete route
  const completeRoute = useCallback(async () => {
    try {
      await axios.post(`${getAPI()}/active-route/complete`);
      setDestinations([]);
      setRoute(null);
      setStartLocation(null);
      setRouteStarted(false);
      setCompleteConfirmOpen(false);
      toast.success('Route completed!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to complete route');
    }
  }, []);

  // Send ETA via WhatsApp
  const sendEtaViaWhatsApp = useCallback((index) => {
    const dest = destinations[index];
    if (!dest) return;

    // Calculate ETA based on remaining travel time from current position
    let totalMin = 0;
    const timeAtStop = 5; // 5 minutes per stop
    
    if (route?.legs) {
      // Sum up travel time for remaining legs
      for (let i = 0; i <= index; i++) {
        // Skip completed stops
        if (destinations[i]?.completed && i < index) continue;
        
        if (route.legs[i]) {
          totalMin += route.legs[i].duration_min;
        }
        // Add time at each stop (except current target)
        if (i < index && !destinations[i]?.completed) {
          totalMin += timeAtStop;
        }
      }
    }

    // Calculate ETA range (start time + 30 min buffer)
    const now = new Date();
    const etaStart = new Date(now.getTime() + totalMin * 60 * 1000);
    const etaEnd = new Date(etaStart.getTime() + 30 * 60 * 1000);
    
    // Format times with am/pm (e.g., "10pm", "10:30pm")
    const formatTime = (date) => {
      let hours = date.getHours();
      const minutes = date.getMinutes();
      const ampm = hours >= 12 ? 'pm' : 'am';
      hours = hours % 12;
      hours = hours ? hours : 12; // 0 should be 12
      
      if (minutes === 0) {
        return `${hours}${ampm}`;
      } else {
        return `${hours}:${String(minutes).padStart(2, '0')}${ampm}`;
      }
    };
    
    const startTime = formatTime(etaStart);
    const endTime = formatTime(etaEnd);

    // Create WhatsApp message with time range
    const message = `Hi, we should be with you from ${startTime} - ${endTime}`;
    const encodedMessage = encodeURIComponent(message);
    
    // Open WhatsApp with preloaded message (user selects contact)
    const whatsappUrl = `https://wa.me/?text=${encodedMessage}`;
    window.open(whatsappUrl, '_blank');
  }, [destinations, route]);

  const completedCount = destinations.filter(d => d.completed).length;
  const totalDistance = route?.total_distance_km || 0;
  // Total duration includes travel time + 5 min at each stop (default)
  const timeAtStopDefault = 5;
  const totalDuration = (route?.total_duration_min || 0) + (destinations.length * timeAtStopDefault);
  const allCompleted = completedCount === destinations.length && destinations.length > 0;

  // Calendar component
  const CalendarView = () => (
    <div className="p-4">
      {/* Month Navigation */}
      <div className="flex items-center justify-between mb-4">
        <Button
          variant="ghost"
          size="sm"
          className="rounded-xl"
          onClick={() => setCalendarMonth(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() - 1))}
        >
          <ChevronLeft className="w-5 h-5" />
        </Button>
        <h3 className="font-semibold text-lg text-[#1d1d1f]">
          {calendarMonth.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}
        </h3>
        <Button
          variant="ghost"
          size="sm"
          className="rounded-xl"
          onClick={() => setCalendarMonth(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() + 1))}
        >
          <ChevronRight className="w-5 h-5" />
        </Button>
      </div>
      
      {/* Day headers */}
      <div className="grid grid-cols-7 gap-1 mb-2">
        {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((day, i) => (
          <div key={i} className="text-center text-xs font-semibold text-[#86868b] py-1">
            {day}
          </div>
        ))}
      </div>
      
      {/* Calendar grid */}
      <div className="grid grid-cols-7 gap-1">
        {(() => {
          const year = calendarMonth.getFullYear();
          const month = calendarMonth.getMonth();
          const firstDay = new Date(year, month, 1);
          const lastDay = new Date(year, month + 1, 0);
          const daysInMonth = lastDay.getDate();
          
          let startDay = firstDay.getDay() - 1;
          if (startDay < 0) startDay = 6;
          
          const cells = [];
          
          for (let i = 0; i < startDay; i++) {
            cells.push(<div key={`empty-${i}`} className="aspect-square" />);
          }
          
          for (let day = 1; day <= daysInMonth; day++) {
            const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
            const isHighlighted = highlightedDates.includes(dateStr);
            const isToday = new Date().toISOString().split('T')[0] === dateStr;
            
            cells.push(
              <button
                key={day}
                onClick={() => toggleCalendarDate(dateStr)}
                className={`aspect-square flex items-center justify-center text-sm font-medium rounded-xl transition-all duration-200
                  ${isHighlighted 
                    ? 'bg-[#007aff] text-white shadow-md' 
                    : 'bg-[#f5f5f7] hover:bg-[#e8e8ed] text-[#1d1d1f]'
                  }
                  ${isToday && !isHighlighted ? 'ring-2 ring-[#007aff]' : ''}
                `}
              >
                {day}
              </button>
            );
          }
          
          return cells;
        })()}
      </div>
      
      {/* Legend */}
      <div className="mt-4 pt-4 border-t border-[rgba(0,0,0,0.08)]">
        <div className="flex items-center gap-4 text-xs text-[#86868b]">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded-lg bg-[#007aff]" />
            <span>Highlighted ({highlightedDates.length})</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded-lg ring-2 ring-[#007aff] bg-[#f5f5f7]" />
            <span>Today</span>
          </div>
        </div>
      </div>
    </div>
  );


  // No route assigned
  if (destinations.length === 0) {
    return (
      <div className="app-container" data-testid="driver-view">
        <header className="app-header h-14" style={{ justifyContent: 'flex-start', gap: '8px' }}>
          <div className="logo text-lg flex-shrink-0">
            <Navigation className="w-5 h-5 text-[#007aff]" />
            <span>Route-Z</span>
          </div>
          <span className="text-[10px] bg-[#34c759] text-white px-1.5 py-0.5 font-semibold uppercase rounded-md">Driver</span>
          <div className="flex items-center gap-1 ml-auto">
            <Button variant="ghost" size="sm" onClick={logout} className="p-2" title="Logout">
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </header>

        {/* Tabs - Icons only */}
        <div className="flex border-b border-[rgba(0,0,0,0.08)] bg-white/80 backdrop-blur-xl">
          <button
            onClick={() => setActiveTab('route')}
            className={`flex-1 py-3 flex items-center justify-center border-b-2 transition-all duration-200 ${
              activeTab === 'route' ? 'border-[#007aff] text-[#007aff]' : 'border-transparent text-[#86868b] hover:text-[#1d1d1f]'
            }`}
            title="Route"
          >
            <Navigation className="w-5 h-5" />
          </button>
          <button
            onClick={() => setActiveTab('calendar')}
            className={`flex-1 py-3 flex items-center justify-center gap-1.5 border-b-2 transition-all duration-200 ${
              activeTab === 'calendar' ? 'border-[#007aff] text-[#007aff]' : 'border-transparent text-[#86868b] hover:text-[#1d1d1f]'
            }`}
            title="Calendar"
          >
            <Calendar className="w-5 h-5" />
            {highlightedDates.length > 0 && <span className="text-[10px] bg-[#f5f5f7] px-1.5 py-0.5 rounded-full font-medium">{highlightedDates.length}</span>}
          </button>
        </div>
        
        {activeTab === 'route' ? (
          <div className="flex flex-col items-center justify-center flex-1 p-8 text-center">
            <Truck className="w-16 h-16 text-[#86868b] mb-4" />
            <h2 className="text-2xl font-semibold text-[#1d1d1f] mb-2">No Route Assigned</h2>
            <p className="text-[#86868b]">Waiting for admin to create your route...</p>
          </div>
        ) : (
          <CalendarView />
        )}
      </div>
    );
  }

  return (
    <div className="app-container" data-testid="driver-view">
      {/* Header */}
      <header className="app-header h-14" style={{ justifyContent: 'flex-start', gap: '8px' }}>
        <div className="logo text-lg flex-shrink-0">
          <Navigation className="w-5 h-5 text-[#007aff]" />
          <span>Route-Z</span>
        </div>
        <span className="text-[10px] bg-[#34c759] text-white px-1.5 py-0.5 font-semibold uppercase rounded-md">Driver</span>
        <div className="flex items-center gap-1 ml-auto">
          <Button variant="ghost" size="sm" onClick={() => setMapCollapsed(!mapCollapsed)} className="p-2">
            <Map className={`w-5 h-5 ${mapCollapsed ? 'text-[#86868b]' : 'text-[#007aff]'}`} />
          </Button>
          <Button variant="ghost" size="sm" onClick={logout} className="p-2" title="Logout">
            <LogOut className="w-5 h-5" />
          </Button>
        </div>
      </header>

      {/* Tabs - Icons only */}
      <div className="flex border-b border-[rgba(0,0,0,0.08)] bg-white/80 backdrop-blur-xl">
        <button
          onClick={() => setActiveTab('route')}
          className={`flex-1 py-3 flex items-center justify-center gap-1.5 border-b-2 transition-all duration-200 ${
            activeTab === 'route' ? 'border-[#007aff] text-[#007aff]' : 'border-transparent text-[#86868b] hover:text-[#1d1d1f]'
          }`}
          title="Route"
        >
          <Navigation className="w-5 h-5" />
          {destinations.length > 0 && <span className="text-[10px] bg-[#f5f5f7] px-1.5 py-0.5 rounded-full font-medium">{destinations.length}</span>}
        </button>
        <button
          onClick={() => setActiveTab('calendar')}
          className={`flex-1 py-3 flex items-center justify-center gap-1.5 border-b-2 transition-all duration-200 ${
            activeTab === 'calendar' ? 'border-[#007aff] text-[#007aff]' : 'border-transparent text-[#86868b] hover:text-[#1d1d1f]'
          }`}
          title="Calendar"
        >
          <Calendar className="w-5 h-5" />
          {highlightedDates.length > 0 && <span className="text-[10px] bg-[#f5f5f7] px-1.5 py-0.5 rounded-full font-medium">{highlightedDates.length}</span>}
        </button>
      </div>

      {/* Main content */}
      {activeTab === 'calendar' ? (
        <div className="flex-1 overflow-auto">
          <CalendarView />
        </div>
      ) : (
      <div className={`main-grid ${mapCollapsed ? 'map-collapsed' : ''}`}>
        <aside className="sidebar">
          {/* Stats */}
          {route && (
            <div className="border-b border-[rgba(0,0,0,0.08)]">
              <div className="grid grid-cols-3 gap-[1px] bg-[rgba(0,0,0,0.04)]">
                <div className="bg-white p-3 text-center">
                  <div className="text-[10px] uppercase text-[#86868b] tracking-wide">Distance</div>
                  <div className="font-semibold text-[#1d1d1f]">{totalDistance.toFixed(1)} km</div>
                </div>
                <div className="bg-white p-3 text-center">
                  <div className="text-[10px] uppercase text-[#86868b] tracking-wide">Duration</div>
                  <div className="font-semibold text-[#1d1d1f]">{Math.round(totalDuration)} min</div>
                </div>
                <div className="bg-white p-3 text-center">
                  <div className="text-[10px] uppercase text-[#86868b] tracking-wide">Done</div>
                  <div className="font-semibold text-[#34c759]">{completedCount}/{destinations.length}</div>
                </div>
              </div>
            </div>
          )}

          {/* Destinations list */}
          <div className="sidebar-scroll">
            <div className="p-2 space-y-2">
              {destinations.map((dest, index) => (
                <div 
                  key={dest.id}
                  className={`destination-card flex-col ${dest.completed ? 'completed' : ''} ${activeStop === index ? 'active' : ''}`}
                  data-testid={`driver-dest-${index}`}
                >
                  <div className="flex items-start">
                    <div className="destination-index">{index + 1}</div>
                    <div className="flex-1 py-2 px-3">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-semibold text-[#1d1d1f]">{dest.postcode}</span>
                        {dest.completed && <Check className="w-4 h-4 text-[#34c759]" />}
                      </div>
                      {dest.name && <div className="text-sm text-[#1d1d1f]">{dest.name}</div>}
                      {dest.note && <div className="text-xs text-[#86868b] italic mt-1">Note: {dest.note}</div>}
                      
                      {/* Route leg info */}
                      {route?.legs?.[index] && index < destinations.length - 1 && (
                        <div className="flex items-center gap-2 mt-2 text-xs text-[#86868b]">
                          <ArrowRight className="w-3 h-3" />
                          <span>{route.legs[index].distance_km} km</span>
                          <Clock className="w-3 h-3" />
                          <span>{Math.round(route.legs[index].duration_min)} min</span>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Action buttons - horizontal bar */}
                  <div className="flex border-t border-[rgba(0,0,0,0.06)] mt-1">
                    <button
                      onClick={() => sendEtaViaWhatsApp(index)}
                      className="flex-1 flex items-center justify-center gap-1.5 py-2.5 text-[#25D366] hover:bg-[#25D366]/10 transition-colors text-xs font-medium"
                      data-testid={`driver-whatsapp-${index}`}
                    >
                      <MessageCircle className="w-4 h-4" />
                      <span>WhatsApp</span>
                    </button>
                    <button
                      onClick={() => toggleComplete(index)}
                      className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 transition-colors text-xs font-medium border-l border-[rgba(0,0,0,0.06)] ${dest.completed ? 'text-[#34c759] bg-[#34c759]/5' : 'text-[#86868b] hover:bg-[#f5f5f7]'}`}
                      data-testid={`driver-complete-${index}`}
                    >
                      <CheckSquare className="w-4 h-4" />
                      <span>{dest.completed ? 'Done' : 'Complete'}</span>
                    </button>
                    <button
                      onClick={() => navigateToStop(dest)}
                      className="flex-1 flex items-center justify-center gap-1.5 py-2.5 text-[#007aff] hover:bg-[#007aff]/10 transition-colors text-xs font-medium border-l border-[rgba(0,0,0,0.06)]"
                      data-testid={`driver-nav-${index}`}
                    >
                      <Navigation className="w-4 h-4" />
                      <span>Navigate</span>
                    </button>
                    <button
                      onClick={() => openNoteModal(index)}
                      className="flex-1 flex items-center justify-center gap-1.5 py-2.5 text-[#86868b] hover:bg-[#f5f5f7] transition-colors text-xs font-medium border-l border-[rgba(0,0,0,0.06)]"
                      data-testid={`driver-note-${index}`}
                    >
                      <Edit2 className="w-4 h-4" />
                      <span>Note</span>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Action button */}
          <div className="flex-shrink-0 p-3 pb-4 border-t border-[rgba(0,0,0,0.08)] bg-white">
            {!routeStarted ? (
              <Button
                onClick={startRoute}
                className="w-full h-14 text-base font-semibold bg-[#007aff] hover:bg-[#0066d6] rounded-xl transition-all duration-200"
                data-testid="driver-start-route-btn"
              >
                <Play className="w-5 h-5 mr-2" />
                Start Route
              </Button>
            ) : allCompleted ? (
              <Button
                onClick={() => setCompleteConfirmOpen(true)}
                className="w-full h-14 text-base font-semibold bg-[#34c759] hover:bg-[#2db84d] rounded-xl transition-all duration-200"
                data-testid="driver-complete-route-btn"
              >
                <CheckCircle className="w-5 h-5 mr-2" />
                Complete Route
              </Button>
            ) : (
              <div className="h-2 bg-[#f5f5f7] rounded-full overflow-hidden">
                <div 
                  className="h-full bg-[#007aff] transition-all duration-300"
                  style={{ width: `${(completedCount / destinations.length) * 100}%` }}
                />
              </div>
            )}
          </div>
        </aside>

        {/* Map */}
        {!mapCollapsed && (
          <div className="map-container" data-testid="driver-map">
            <MapComponent
              destinations={destinations}
              route={route}
              startLocation={startLocation}
              activeDestination={activeStop}
              onDestinationClick={setActiveStop}
              driverLocation={myLocation}
            />
          </div>
        )}
      </div>
      )}

      {/* Note Modal */}
      <Dialog open={noteModalOpen} onOpenChange={setNoteModalOpen}>
        <DialogContent className="max-w-[90vw] sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add Note</DialogTitle>
            <DialogDescription>Add a note for stop #{(editingIndex ?? 0) + 1}</DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Textarea
              placeholder="Enter note..."
              value={editingNote}
              onChange={(e) => setEditingNote(e.target.value)}
              className="min-h-[100px]"
              data-testid="driver-note-input"
              autoFocus
            />
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setNoteModalOpen(false)}>Cancel</Button>
            <Button onClick={saveNote}>Save Note</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Complete Route Confirmation */}
      <AlertDialog open={completeConfirmOpen} onOpenChange={setCompleteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Complete Route?</AlertDialogTitle>
            <AlertDialogDescription>
              Great job! All {destinations.length} stops are done. Mark this route as complete?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={completeRoute} className="bg-green-600 hover:bg-green-700">Complete Route</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
